import { useLoadScript } from '@react-google-maps/api';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalBody } from 'reactstrap';

import MapsAutocompleteDropdown from '../../components/MapsAutocompleteDropdown/MapsAutocompleteDropdown';
import Select from '../../components/ReactSelectDropdown';
import { filterOption } from '../../components/ReactSelectDropdown/ReactSelectDropdown';
import useAutocompleteService from '../../customHooks/usePlacesAutocompleteService';
import {
  ColoredButton,
  PrimaryButton,
  RadiusButton,
  WhiteButton,
} from '../../styledComponents/buttons';
import {
  CommonFlex,
  CommonGrid,
  CommonText,
  FormAlert,
  InputField,
  InputLabel,
  InputWithLabelSection,
  RedStar,
} from '../../styledComponents/common';
import { checkHtml, checkUrl } from '../../utils/helper';
import TaskLocationMap from '../Tasks/TaskLocationMap';
import {
  AddTaskLocationInfoContainer,
  AddTaskLocationSection,
} from '../Tasks/CreateTask/createTaskStyles'
import CreateProjectDropdown from '../../components/DropdownStyle/CreateProjectDropdown';
import ButtonTextLoader from '../../components/Loaders/ButtonTextLoader';

const mapLibraries = ['places'];
const mapIds = [process.env.REACT_APP_JOB_SITES_MAP_ID];

const AddGeofenceLocationModal = ({
  isOpen,
  toggle,
  createdTaskLocationInfo,
  addGeofenceLocation,
  addGeofenceLocationLoading,
  onLocationChange,
  taskLocation,
  taskLocationUpdate,
  updateGeofenceLocation,
  updateGeofenceLocationLoading,
  editData
}) => {
  const { t } = useTranslation();


  const [locationName, setLocationName] = useState('');
  const [locationAddress, setLocationAddress] = useState('');
  const [radiusInput, setRadiusInput] = useState(100);
  const [saveErrors, setSaveErrors] = useState({});
  const radiusOptions = [100, 150, 200, 300, { label: t('custom'), value: 'custom' }];
  const [isCreated, setIsCreated] = useState(false);
  const [addressMarker, setAddressMarker] = useState(null);
  const [isCustomRadius, setIsCustomRadius] = useState(false);
  const [isUserInitiatedRadiusChange, setIsUserInitiatedRadiusChange] = useState(false);

  const [selectedTaskType, setSelectedTaskType] = useState(null);
  const [locationList, setLocationList] = useState([
    {
      label: t('circle_type'),
      value: 'circle',
    },
    {
      label: t('polygon_type'),
      value: 'polygon',
    },
  ]);

  // Track initial values for comparison
  const [initialValues, setInitialValues] = useState({
    name: '',
    address: '',
    radius: 100,
    locationType: null,
  });

  // map states
  const [map, setMap] = useState(null);

  const [userCurrentLocation, setUserCurrentLocation] = useState(null);

  const { isLoaded, loadError } = useLoadScript({
    id: 'job-site-details-map-script-loader',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_JAVASCRIPT_API_KEY,
    libraries: mapLibraries,
    mapIds,
  });

  const {
    geocoderService,
    getPlacePredictions,
    placePredictions,
    setPlacePredictions,
    isPredictionsLoading,
    refreshSessionToken,
  } = useAutocompleteService({ map, debounceTimeout: 500 });

  // Add state for polygon vertices
  const [polygonVertices, setPolygonVertices] = useState([]);

  useEffect(() => {
    if (isOpen) {
      if (!taskLocationUpdate) {
        setDefaultLocation();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (taskLocationUpdate && taskLocation) {
      setLocationName(taskLocation.name || '');
      setLocationAddress(taskLocation.address || '');

      // Check if the radius is a custom value (not in the predefined options)
      const isCustomRadiusValue = !radiusOptions.some(option =>
        (typeof option === 'number' && option === taskLocation.radius) ||
        (option.value === 'custom' && taskLocation.radius >= 100)
      );

      // Set the radius and custom radius flag
      setRadiusInput(taskLocation.radius < 100 ? 100 : taskLocation.radius);
      setIsCustomRadius(isCustomRadiusValue);

      // Set location type based on the task location type
      const locationType = taskLocation.type === 'polygonal' ? 'polygon' : 'circle';
      setSelectedTaskType({
        label: locationType === 'polygon' ? t('polygon_type') : t('circle_type'),
        value: locationType
      });

      // Handle location type specific data
      if (taskLocation.type === 'circular') {
        handleSetAddressMarker({
          lat: parseFloat(taskLocation.latitude) || 0,
          lng: parseFloat(taskLocation.longitude) || 0,
        });
      } else if (taskLocation.type === 'polygonal' && taskLocation.vertices) {
        setPolygonVertices(taskLocation.vertices);
      }

      // Set initial values for comparison
      setInitialValues({
        name: taskLocation.name || '',
        address: taskLocation.address || '',
        radius: taskLocation.radius || 100,
        locationType: taskLocation.type === 'polygonal' ? 'polygon' : 'circle',
      });
    }
  }, [taskLocationUpdate, taskLocation]);

  useEffect(() => {
    fetch('https://hutils.loxal.net/whois')
      .then(resp => resp.json())
      .then(res => {
        if (res && res.ip) {
          setUserCurrentLocation({
            lat: res.latitude,
            lng: res.longitude,
          });
        }
      })
      .catch(err => {
        console.log(err);
      });
  }, []);

  useEffect(() => {
    if (map && userCurrentLocation) {
      map.panTo(userCurrentLocation);
    }
  }, [map, userCurrentLocation]);

  useEffect(() => {
    if (
      createdTaskLocationInfo &&
      createdTaskLocationInfo.id &&
      isOpen &&
      isCreated
    ) {
      handleToggle();
      // getTaskLocation();
      onLocationChange &&
        onLocationChange({
          label: createdTaskLocationInfo.name,
          value: createdTaskLocationInfo.id,
          ...createdTaskLocationInfo,
        });
    }
  }, [createdTaskLocationInfo]);

  const handleGetGeocode = place => {
    if (geocoderService && place && place.place_id && map) {
      setLocationAddress(place.description);
      geocoderService
        .geocode({ placeId: place.place_id })
        .then(({ results }) => {
          handleSetAddressMarker(results[0].geometry.location);
        })
        .catch(e => {
          console.log(e);
        });
      setPlacePredictions([]);
    }
    refreshSessionToken();
  };

  const handleReverseGeocode = coord => {
    if (geocoderService && coord) {
      setPlacePredictions([]);
      geocoderService
        .geocode({ location: coord })
        .then(results => {
          if (results.results && results.results.length) {
            setLocationAddress(results.results[0].formatted_address);
          } else {
            setLocationAddress(`${coord.lat()}, ${coord.lng()}`);
          }
        })
        .catch(e => {
          console.log(e);
        });
    }
  };

  const handleSetAddressFromMap = location => {
    if (location && location.latLng) {
      handleSetAddressMarker(location.latLng);
      handleReverseGeocode(location.latLng);
    }
  };
  const handleSetAddressMarker = location => {
    if (location) {
      setAddressMarker(location);
      handleMapPanAndZoomToSingleLocation(location);
    }
  };
  const handleMapPanAndZoomToSingleLocation = location => {
    if (map) {
      map.panTo(location);
      map.setZoom(17);
    }
  };

  const onLocationNameChange = e => {
    const { value } = e.target;
    setLocationName(value);
    if (saveErrors && saveErrors['locationName']) {
      delete saveErrors['locationName'];
    }
  };

  const onLocationAddressChange = e => {
    const { value } = e.target;
    getPlacePredictions({ input: value });
    setLocationAddress(value);
    if (saveErrors && saveErrors['locationAddress']) {
      delete saveErrors['locationAddress'];
    }
  };

  const checkError = () => {
    let saveErrors = {};
    let formIsValid = true;
    let failedChecks = [];

    if (checkUrl(locationName)) {
      formIsValid = false;
      saveErrors['locationName'] = 'Geofence location name cannot contain a URL';
      failedChecks.push('locationName: URL check failed');
    } else if (checkHtml(locationName)) {
      formIsValid = false;
      saveErrors['locationName'] = 'Invalid geofence location name';
      failedChecks.push('locationName: HTML check failed');
    }

    if (checkUrl(locationAddress)) {
      formIsValid = false;
      saveErrors['locationAddress'] = 'Geofence location address cannot contain a URL';
      failedChecks.push('locationAddress: URL check failed');
    } else if (checkHtml(locationAddress)) {
      formIsValid = false;
      saveErrors['locationAddress'] = 'Invalid geofence location address';
      failedChecks.push('locationAddress: HTML check failed');
    }

    if (selectedTaskType?.value === 'circle' && !radiusInput) {
      formIsValid = false;
      saveErrors['radius'] = 'Radius is required';
      failedChecks.push('radius: Required check failed');
    } else if (selectedTaskType?.value === 'circle' && radiusInput % 1 !== 0) {
      formIsValid = false;
      saveErrors['radius'] = 'Fraction is not allowed for radius';
      failedChecks.push('radius: Fraction check failed');
    } else if (selectedTaskType?.value === 'circle' && radiusInput < 100) {
      formIsValid = false;
      saveErrors['radius'] = 'Minimum value for radius is 100 meters';
      failedChecks.push('radius: Minimum value check failed');
    }

    if (!selectedTaskType || !selectedTaskType.value) {
      formIsValid = false;
      saveErrors['locationType'] = 'Location type is required';
      failedChecks.push('locationType: Required check failed');
    }

    if (selectedTaskType?.value === 'polygon' && (!polygonVertices || polygonVertices.length < 3)) {
      formIsValid = false;
      saveErrors['polygon'] = 'Polygon must have at least 3 vertices';
      failedChecks.push('polygon: Minimum vertices check failed');
    }

    setSaveErrors(saveErrors);

    // Log the failed checks if any
    if (failedChecks.length > 0) {
      console.log('Validation failed for the following checks:', failedChecks);
    } else {
      console.log('All validation checks passed');
    }

    return formIsValid;
  };

  const isLocationDataComplete = () => {
    const baseConditions =
      locationName?.trim() &&
      locationAddress?.trim() &&
      selectedTaskType?.value;

    if (selectedTaskType?.value === 'circle') {
      return baseConditions &&
        addressMarker &&
        radiusInput >= 50;
    } else {
      return baseConditions && polygonVertices && polygonVertices.length >= 3;
    }
  };

  const handleAddGeofenceLocation = async () => {
    if (!checkError()) {
      return;
    }

    let payload = {
      name: locationName,
      address: locationAddress,
      type: selectedTaskType?.value === 'polygon' ? 'polygonal' : 'circular',
    };

    // Add type-specific data
    if (selectedTaskType?.value === 'circle') {
      payload = {
        ...payload,
        latitude: addressMarker.lat,
        longitude: addressMarker.lng,
        radius: parseFloat(radiusInput),
      };
    } else if (selectedTaskType?.value === 'polygon') {
      payload = {
        ...payload,
        vertices: polygonVertices,
      };
    }

    if (taskLocationUpdate) {
      await updateGeofenceLocation(payload, editData.id);
    } else {
      await addGeofenceLocation(payload);
      // Close modal only on successful addition
      handleToggle();
    }
  };



  const clearState = () => {
    setLocationName('');
    setLocationAddress('');
    setRadiusInput(100);
    setSaveErrors({});
    setAddressMarker(null);
    setMap(null);
    setUserCurrentLocation(null);
    setIsCreated(false);
    setSelectedTaskType(null);
    setPolygonVertices([]); // Clear polygon vertices
    setIsUserInitiatedRadiusChange(false); // Reset the user-initiated flag
    setInitialValues({
      name: '',
      address: '',
      radius: 100,
      locationType: null,
    });
  };
  const setDefaultLocation = () => {
    setLocationAddress('Mecca, Saudi Arabia');
    handleSetAddressMarker({
      lat: parseFloat(21.4240968),
      lng: parseFloat(39.81733639999999),
    });
    setUserCurrentLocation({
      lat: parseFloat(21.4240968),
      lng: parseFloat(39.81733639999999),
    });
  };

  const handleToggle = () => {
    clearState();
    toggle();
  };

  const handleUpdateTaskLocation = () => {
    if (!isLocationDataComplete()) {
      setSaveErrors({
        ...saveErrors,
        general: 'Please ensure all location details are properly filled'
      });
      return;
    }

    if (checkError()) {
      const payload = {
        id: taskLocation.id,
        name: locationName,
        address: locationAddress,
        type: selectedTaskType?.value === 'polygon' ? 'polygonal' : 'circular',
      };

      if (selectedTaskType?.value === 'circle') {
        if (!addressMarker) {
          setSaveErrors({
            ...saveErrors,
            general: 'Please select a location on the map'
          });
          return;
        }

        payload.latitude = typeof addressMarker.lat === 'function' ? addressMarker.lat() : addressMarker.lat;
        payload.longitude = typeof addressMarker.lng === 'function' ? addressMarker.lng() : addressMarker.lng;
        payload.radius = radiusInput;
        payload.vertices = null; // Explicitly clear polygon data
      } else {
        if (!polygonVertices || polygonVertices.length < 3) {
          setSaveErrors({
            ...saveErrors,
            general: 'Please complete the polygon with at least 3 points'
          });
          return;
        }

        payload.vertices = polygonVertices;
        // Clear circle data
        payload.latitude = null;
        payload.longitude = null;
        payload.radius = null;
      }

      updateGeofenceLocation(payload);
      // Reset the user-initiated flag after update
      setIsUserInitiatedRadiusChange(false);
    }
  };

  const isUpdateButtonDisabled = () => {
    // Check if radius has changed by comparing current and initial values
    const hasRadiusChanged = radiusInput !== initialValues.radius;

    return (
      locationName === initialValues.name &&
      locationAddress === initialValues.address &&
      !hasRadiusChanged && // Simplified radius comparison
      selectedTaskType?.value === initialValues.locationType
    );
  };

  // Add effect to handle location type changes
  useEffect(() => {
    if (taskLocationUpdate && taskLocation) {
      if (selectedTaskType?.value === 'circle') {
        // When switching to circle type
        setPolygonVertices([]); // Clear polygon data

        // Try to set the address marker from the task location
        if (taskLocation.latitude && taskLocation.longitude) {
          try {
            const lat = parseFloat(taskLocation.latitude);
            const lng = parseFloat(taskLocation.longitude);

            // Check if the values are valid numbers
            if (!isNaN(lat) && !isNaN(lng)) {
              handleSetAddressMarker({
                lat: lat,
                lng: lng
              });
            } else {
              // If invalid coordinates, try to geocode the address
              console.warn("Invalid coordinates, trying to geocode address");
              if (locationAddress) {
                geocoderService.geocode({ address: locationAddress })
                  .then(results => {
                    if (results.results && results.results.length > 0) {
                      const location = results.results[0].geometry.location;
                      handleSetAddressMarker({
                        lat: location.lat(),
                        lng: location.lng()
                      });
                    }
                  })
                  .catch(err => {
                    console.error("Geocoding error:", err);
                  });
              }
            }
          } catch (error) {
            console.error("Error setting address marker:", error);
            // If there's an error, try to geocode the address
            if (locationAddress) {
              geocoderService.geocode({ address: locationAddress })
                .then(results => {
                  if (results.results && results.results.length > 0) {
                    const location = results.results[0].geometry.location;
                    handleSetAddressMarker({
                      lat: location.lat(),
                      lng: location.lng()
                    });
                  }
                })
                .catch(err => {
                  console.error("Geocoding error:", err);
                });
            }
          }
        } else {
          // If no coordinates available, use the address to set the marker
          if (locationAddress) {
            geocoderService.geocode({ address: locationAddress })
              .then(results => {
                if (results.results && results.results.length > 0) {
                  const location = results.results[0].geometry.location;
                  handleSetAddressMarker({
                    lat: location.lat(),
                    lng: location.lng()
                  });
                }
              })
              .catch(err => {
                console.error("Geocoding error:", err);
              });
          }
        }
        setRadiusInput(taskLocation.radius < 100 ? 100 : taskLocation.radius);
      } else if (selectedTaskType?.value === 'polygon') {
        // When switching to polygon type
        setAddressMarker(null); // Clear circle data
        setRadiusInput(100); // Reset radius to default

        // Reset polygon drawing state
        if (taskLocation.vertices && taskLocation.vertices.length >= 3) {
          setPolygonVertices(taskLocation.vertices);
        } else {
          // If no valid vertices, reset to empty state
          setPolygonVertices([]);
        }

        // Reset any errors related to polygon
        if (saveErrors && saveErrors['polygon']) {
          const updatedErrors = { ...saveErrors };
          delete updatedErrors['polygon'];
          setSaveErrors(updatedErrors);
        }
      }
    }
  }, [selectedTaskType, taskLocation]);

  // Update the radius section to handle custom radius
  const renderRadiusSection = () => {
    if (selectedTaskType?.value !== 'circle') return null;

    return (
      <InputWithLabelSection margin="20px 0 0">
        <CommonGrid>
          <InputLabel>
            {t('location_radius')} <RedStar>*</RedStar>
          </InputLabel>
        </CommonGrid>
        <CommonFlex justifyContent="start" gap="12px" wrap="wrap">
          {radiusOptions.map((option) => (
            <RadiusButton
              key={option.value || option}
              active={isCustomRadius ? option.value === 'custom' : radiusInput === option}
              onClick={() => {
                if (option.value === 'custom') {
                  setIsCustomRadius(true);
                  setIsUserInitiatedRadiusChange(true);
                  setRadiusInput(110);
                } else {
                  setIsCustomRadius(false);
                  setIsUserInitiatedRadiusChange(true);
                  setRadiusInput(option);
                  if (saveErrors && saveErrors['radius']) {
                    const updatedErrors = { ...saveErrors };
                    delete updatedErrors['radius'];
                    setSaveErrors(updatedErrors);
                  }
                }
              }}
            >
              {option.label || `${option} m`}
            </RadiusButton>
          ))}
        </CommonFlex>
        {isCustomRadius && (
          <InputField
            type="text"
            placeholder={t('enter_custom_radius')}
            value={radiusInput ? `${radiusInput} m` : '110 m'}
            disabled={true}
            onChange={e => {
              const value = parseInt(e.target.value);
              // Only update if value is >= 100 or empty (to allow clearing)
              if (value >= 100 || e.target.value === '') {
                setIsUserInitiatedRadiusChange(true);
                setRadiusInput(value || 100); // Default to 100 if empty
                if (saveErrors && saveErrors['radius']) {
                  delete saveErrors['radius'];
                }
              }
            }}
            min="100"
            style={{ marginTop: '10px' }}
          />
        )}
        {saveErrors &&
          saveErrors['radius'] &&
          saveErrors['radius'] !== undefined && (
            <FormAlert>{saveErrors['radius']}</FormAlert>
          )
        }
      </InputWithLabelSection>
    );
  };


  // Add effect to automatically update when radius changes in edit mode
  useEffect(() => {
    if (map && selectedTaskType?.value === 'circle') {
      const location = addressMarker || userCurrentLocation;
      if (location) {
        handleMapPanAndZoomToSingleLocation(location);
      }
    }

    const predefinedRadius = [100, 150, 200, 300];
    if (predefinedRadius.includes(radiusInput)) {
      setIsCustomRadius(false);
    } else {
      setIsCustomRadius(true);
    }

    // Skip this effect if we're not in edit mode or if the radius hasn't changed
    if (!taskLocationUpdate || selectedTaskType?.value !== 'circle') {
      return;
    }

    // Skip if the radius hasn't changed from initial value
    if (radiusInput === initialValues.radius) {
      return;
    }

    // Skip if the change wasn't user-initiated
    if (!isUserInitiatedRadiusChange) {
      return;
    }

    // Skip if the form is incomplete
    if (!isLocationDataComplete()) {
      return;
    }

    // Skip if the update button would be disabled (no changes)
    if (isUpdateButtonDisabled()) {
      return;
    }



  }, [radiusInput, taskLocationUpdate, selectedTaskType, isUserInitiatedRadiusChange]);

  // Add effect to focus on the location when the map is loaded
  useEffect(() => {
    if (map && taskLocationUpdate && taskLocation) {
      if (taskLocation.type === 'circular') {
        // For circle type, focus on the center point
        const center = {
          lat: parseFloat(taskLocation.latitude) || 0,
          lng: parseFloat(taskLocation.longitude) || 0,
        };
        map.panTo(center);
        map.setZoom(17);
      } else if (taskLocation.type === 'polygonal' && taskLocation.vertices && taskLocation.vertices.length >= 3) {
        // For polygon type, calculate the bounds of the polygon
        const bounds = new google.maps.LatLngBounds();
        taskLocation.vertices.forEach(vertex => {
          bounds.extend(new google.maps.LatLng(vertex[0], vertex[1]));
        });

        // Fit the map to the bounds with some padding
        map.fitBounds(bounds, {
          padding: { top: 50, right: 50, bottom: 50, left: 50 }
        });
      }
    }
  }, [map, taskLocationUpdate, taskLocation]);

  // Handle radius change from the map
  const handleRadiusChange = (newRadius) => {
    if (selectedTaskType?.value === 'circle') {
      // Only update if the radius has actually changed by more than 5 meters
      // This prevents excessive updates for small changes
      if (Math.abs(newRadius - radiusInput) > 5) {
        // Set the custom radius flag if the new radius is not in the predefined options
        const isCustomValue = !radiusOptions.some(option =>
          (typeof option === 'number' && option === newRadius) ||
          (option.value === 'custom' && newRadius >= 100)
        );

        if (isCustomValue) {
          setIsCustomRadius(true);
        }

        setIsUserInitiatedRadiusChange(true);
        setRadiusInput(newRadius);
      }
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleToggle}
      style={{ marginTop: `2vh`, maxWidth: `1250px` }}
    >
      <ModalBody style={{ padding: `0` }}>
        <AddTaskLocationSection>
          <AddTaskLocationInfoContainer>
            <CommonText name title fontSize="16px" margin="auto 0 10px 0">
              {taskLocationUpdate ? t('update_geofence_location') : t('add_geofence_location')}
            </CommonText>

            <InputWithLabelSection margin="20px 0 0">
              <InputLabel>
                {t('location_name')} <RedStar>*</RedStar>
              </InputLabel>
              <InputField
                type="text"
                placeholder={t('type_location_name')}
                value={locationName}
                onChange={e => onLocationNameChange(e)}
                style={
                  saveErrors &&
                  saveErrors[`locationName`] && {
                    border: `1px solid #fe5969`,
                    backgroundColor: `rgba(252, 87, 104, 0.05)`,
                  }
                }
              />
              {saveErrors &&
                saveErrors['locationName'] &&
                saveErrors['locationName'] !== undefined && (
                  <FormAlert>{saveErrors['locationName']}</FormAlert>
                )}
            </InputWithLabelSection>

            <InputWithLabelSection margin="20px 0 0">
              <InputLabel>
                {t('location_type')}
              </InputLabel>
              <Select
                isSearchable
                value={selectedTaskType}
                options={locationList}
                placeholder={t('select_task_location_type')}
                onChange={e => {
                  setSelectedTaskType(e);
                }}
                styles={CreateProjectDropdown()}
                filterOption={filterOption}
              />
            </InputWithLabelSection>

            <InputWithLabelSection
              style={{ position: 'relative' }}
              margin="20px 0 0"
            >
              <InputLabel>
                {t('location_address')} <RedStar>*</RedStar>
              </InputLabel>
              <InputField
                type="text"
                placeholder={t('type_location_address')}
                value={locationAddress}
                onChange={e => onLocationAddressChange(e)}
                style={
                  saveErrors &&
                  saveErrors[`locationAddress`] && {
                    border: `1px solid #fe5969`,
                    backgroundColor: `rgba(252, 87, 104, 0.05)`,
                  }
                }
              />
              {saveErrors &&
                saveErrors['locationAddress'] &&
                saveErrors['locationAddress'] !== undefined && (
                  <FormAlert>{saveErrors['locationAddress']}</FormAlert>
                )}
              {!isPredictionsLoading &&
                placePredictions &&
                placePredictions.length &&
                locationAddress ? (
                <MapsAutocompleteDropdown
                  dropdownOptions={placePredictions}
                  handleSelect={handleGetGeocode}
                />
              ) : null}
            </InputWithLabelSection>

            {/* Render radius section conditionally */}
            {renderRadiusSection()}
            <CommonGrid
              justifyContent="unset"
              padding="10px 0 0 0"
              margin="30px 0 0"
            >
              <WhiteButton onClick={() => handleToggle()}>
                {t('cancel')}
              </WhiteButton>
              {taskLocationUpdate ? (
                <PrimaryButton
                  disabled={!isLocationDataComplete() || isUpdateButtonDisabled()}
                  onClick={handleUpdateTaskLocation}
                >
                  {updateGeofenceLocationLoading ? (
                    <ButtonTextLoader
                      loadingText={t('update_location')}
                      fontSize="13px"
                    />
                  ) : (
                    t('update_location')
                  )}
                </PrimaryButton>
              ) : (
                <PrimaryButton
                  disabled={!isLocationDataComplete()}
                  onClick={handleAddGeofenceLocation}
                >
                  {addGeofenceLocationLoading ? (
                    <ButtonTextLoader
                      loadingText={t('add_location')}
                      fontSize="13px"
                    />
                  ) : (
                    t('add_location')
                  )}
                </PrimaryButton>
              )}
              {saveErrors.general && (
                <FormAlert style={{ marginTop: '10px', textAlign: 'center' }}>
                  {saveErrors.general}
                </FormAlert>
              )}
            </CommonGrid>
          </AddTaskLocationInfoContainer>
          <TaskLocationMap
            setMap={setMap}
            singleAddressMarker={addressMarker}
            setAddressFromMap={handleSetAddressFromMap}
            radius={radiusInput}
            setRadius={handleRadiusChange}
            locationType={selectedTaskType?.value}
            onPolygonComplete={setPolygonVertices}
            isEditing={taskLocationUpdate}
            existingVertices={taskLocationUpdate && taskLocation?.vertices ? taskLocation.vertices : []}
          />
        </AddTaskLocationSection>
      </ModalBody>
    </Modal>
  );
};

export default AddGeofenceLocationModal;
